import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import QuillTableBetter from 'quill-table-better';

import range from 'helper/range';

import 'quill/dist/quill.snow.css';
import './index.scss';

const FONT_SIZES = range(20).map((i) => `${8 + i * 2}px`);

// This is necessary to have Quill use inline styles instead of its "ql" classes
const Size = Quill.import('attributors/style/size') as any;
Size.whitelist = FONT_SIZES;
Quill.register(Size, true);
Quill.register(Quill.import('attributors/attribute/direction') as any, true);
Quill.register(Quill.import('attributors/style/align') as any, true);
Quill.register(Quill.import('attributors/style/background') as any, true);
Quill.register(Quill.import('attributors/style/color') as any, true);
Quill.register({ 'modules/table-better': QuillTableBetter }, true);

const quillModules = {
  toolbar: [
    [{ header: [false, 1, 2] }],
    [{ size: [false, ...FONT_SIZES] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['link', 'image'],
    ['clean'],
  ],
  table: false,
  'table-better': {
    language: 'en_US',
    menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
    toolbarTable: true,
  },
  keyboard: {
    bindings: QuillTableBetter.keyboardBindings,
  },
};

const parseQuillContent = (baseContent: string) => (
  baseContent
    .replaceAll(/&nbsp;/g, ' ')
    .replaceAll(/<p><\/p>/g, '<p><br></p>')
);

function RichTextEditor({
  id = '',
  value,
  onChange,
}: {
  id?: string,
  value: string | undefined,
  onChange: React.Dispatch<React.SetStateAction<string>> | ((v: string) => void),
}) {
  const [content, setContent] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return () => {};

    const editorContainer = container.appendChild(
      container.ownerDocument.createElement('div'),
    );
    quillRef.current = new Quill(editorContainer, {
      theme: 'snow',
      modules: quillModules,
    });

    return () => { container.innerHTML = ''; };
  }, []);

  useEffect(() => {
    if (!quillRef.current) return;

    const quill = quillRef.current;
    quill.on(Quill.events.TEXT_CHANGE, () => {
      setContent(quill.getSemanticHTML());
      setIsUpdating(true);
    });
  }, [setContent, setIsUpdating]);

  useEffect(() => {
    if (isUpdating) return;

    const quill = quillRef.current;
    if (!quill) return;

    if (value !== parseQuillContent(content)) {
      const delta = quill.clipboard.convert({ html: value });
      quill.setContents([{ insert: '' }]);
      quill.updateContents(delta, Quill.sources.USER);
    }
  }, [value, content, isUpdating]);

  useEffect(() => {
    if (isUpdating) {
      onChange(parseQuillContent(content));
      setIsUpdating(false);
    }
  }, [isUpdating, content, onChange, setIsUpdating]);

  return (
    <div ref={containerRef} id={id} />
  );
}

export default RichTextEditor;
